import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

function AnimatedArrow({ direction, delay }: { direction: string; delay: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: direction === "up" ? 10 : -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
    >
      {direction === "up" ? (
        <TrendingUp className="w-8 h-8 text-success" />
      ) : (
        <TrendingDown className="w-8 h-8 text-success" />
      )}
    </motion.div>
  );
}

export function ImpactSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const { t } = useLanguage();

  const metrics = [
    {
      label: t("impact.metric.1"),
      direction: "down",
    },
    {
      label: t("impact.metric.2"),
      direction: "up",
    },
    {
      label: t("impact.metric.3"),
      direction: "up",
    },
    {
      label: t("impact.metric.4"),
      direction: "up",
    },
  ];

  return (
    <section className="py-20 lg:py-32">
      <div className="section-container" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("impact.title")}{" "}
            <span className="text-primary">{t("impact.titleHighlight")}</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric, index) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="card-surface p-6 lg:p-8 text-center hover-glow"
            >
              <div className="flex justify-center mb-4">
                {isInView && (
                  <AnimatedArrow direction={metric.direction} delay={index * 0.15 + 0.3} />
                )}
              </div>
              <p className="text-lg font-semibold text-foreground">
                {metric.label}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {metric.direction === "up" ? "↑" : "↓"}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
